import { useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
// import Search from "./Search";
import { memo } from "react";
import { logoutAction } from "../../redux/actions/authActions";
import { IoLogOutOutline } from "react-icons/io5";
import { IoSettingsOutline } from "react-icons/io5";
import { Transition } from "@headlessui/react";
import { HiChevronDown } from "react-icons/hi2";
import { FaUsers, FaBook } from "react-icons/fa";
import Logo from "../../assets/SocialEcho.png";
import Favicon from "../../assets/favicon-32x32.png";
import { HiNewspaper } from "react-icons/hi";
import NotificationBell from "../notifications/NotificationBell";

const Navbar = ({ userData }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSearchPage = location.pathname === "/skills-feed/search";
  
  // Search state - initialize from URL if on search page
  const [searchValue, setSearchValue] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [communitySuggestions, setCommunitySuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);

  async function fetchSuggestions(q) {
    const { searchUsers } = await import("../../redux/api/userAPI");
    const { getAllCommunities } = await import("../../redux/api/communityAPI");
    const [{ data: usersData }, { data: communitiesData }] = await Promise.all([
      searchUsers(q, 1),
      getAllCommunities(),
    ]);
    setUserSuggestions((usersData?.users || usersData || []).slice(0, 5));
    const filteredCommunities = (communitiesData || []).filter(c => (c?.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 5);
    setCommunitySuggestions(filteredCommunities);
  }

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchValue(v);
    if (v.trim()) {
      setShowSuggest(true);
      clearTimeout(window.__sb);
      window.__sb = setTimeout(() => fetchSuggestions(v.trim()), 250);
    } else {
      setShowSuggest(false);
      setUserSuggestions([]);
      setCommunitySuggestions([]);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchValue.trim()) {
      navigate(`/skills-feed/search?q=${encodeURIComponent(searchValue.trim())}`);
      setShowSuggest(false);
    }
  };

  // Sync search value with URL when on search page
  useEffect(() => {
    if (isSearchPage) {
      const urlQuery = searchParams.get("q") || "";
      setSearchValue(urlQuery);
    } else {
      // Clear search when leaving search page
      setSearchValue("");
      setShowSuggest(false);
    }
  }, [searchParams, isSearchPage]);

  const dispatch = useDispatch();
  const [showDropdown, setShowDropdown] = useState(false);
  const [avatar, setAvatar] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "{}");
      return stored?.user?.avatar || userData?.avatar || "";
    } catch {
      return userData?.avatar || "";
    }
  });

  const dropdownRef = useRef(null);

  const handleProfileClick = () => {
    setShowDropdown(!showDropdown);
  };

  const logout = async () => {
    await dispatch(logoutAction());
    // Redirect to auth page after logout
    navigate("/auth");
  };

  const searchRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggest(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  // One-time DP fetch on first render to cache and persist absolute avatar URL
  useEffect(() => {
    (async () => {
      try {
        const storedRaw = localStorage.getItem("profile");
        if (!storedRaw) return;
        const profile = JSON.parse(storedRaw);
        const user = profile?.user || {};
        setAvatar(user?.avatar || "");
      } catch {}
    })();
  }, []);

  return (
    <nav className="sticky top-0 z-20 mb-0 border bg-white p-0 font-heading">
      <div className="max-w-7xl mx-auto flex justify-between items-center px-4 lg:px-6">
        <div className="flex items-center">
          <Link to="/skills-feed" className="flex items-center gap-2">
            <img className="w-10 h-10 md:hidden" src={Favicon} alt="SkillConnect" />
            <img className="hidden md:block w-48 h-auto" src={Logo} alt="SkillConnect" />
          </Link>
        </div>

        {userData && (
          <div className="hidden md:flex flex-1 justify-start pl-10 pr-2">
            <div ref={searchRef} className="relative w-full max-w-lg">
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search people or communities..."
              className="w-full px-4 py-2 pl-10 pr-4 text-sm bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder-gray-500 font-roboto-flex"
            />
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>

            {showSuggest && (userSuggestions.length > 0 || communitySuggestions.length > 0) && (
              <div className="absolute mt-2 w-full bg-white border rounded-lg shadow-lg z-30 overflow-hidden">
                {userSuggestions.length > 0 && (
                  <div className="py-2">
                    <div className="px-3 py-1 text-xs uppercase text-gray-400">People</div>
                    {userSuggestions.map(u => (
                      <Link key={u._id} to={`/profile/${u.profileUrl || u._id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50" onClick={() => setShowSuggest(false)}>
                        <img src={u.avatar} alt={u.name} className="w-6 h-6 rounded-full object-cover" />
                        <span className="text-sm text-gray-700 truncate">{u.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
                {communitySuggestions.length > 0 && (
                  <div className="py-2 border-t">
                    <div className="px-3 py-1 text-xs uppercase text-gray-400">Communities</div>
                    {communitySuggestions.map(c => (
                      <Link key={c.name} to={`/skills-feed/${c.name}`} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50" onClick={() => setShowSuggest(false)}>
                        <img src={c.banner} alt={c.name} className="w-6 h-6 rounded-full object-cover" />
                        <span className="text-sm text-gray-700 truncate">{c.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        )}

        <div className="relative flex justify-end items-center gap-4 md:gap-6 py-1">
          {/* Navigation icons - only show when logged in */}
          {userData && (
            <>
              {/* Skills Feed Icon */}
              <Link
                to="/skills-feed"
                className={`relative group p-2 rounded-full transition-all duration-200 ${
                  location.pathname === '/skills-feed' || location.pathname === '/'
                    ? 'bg-gray-100'
                    : 'hover:bg-gray-100'
                }`}
                title="Skills Feed"
              >
                <HiNewspaper className={`w-8 h-8 transition-colors duration-200 ${
                  location.pathname === '/skills-feed' || location.pathname === '/'
                    ? 'text-primary-700'
                    : 'text-gray-600 group-hover:text-primary-700'
                }`} />
              </Link>

              {/* Skills Circle Icon */}
              <Link
                to="/skills-circle/received-follow-requests"
                className={`relative group p-2 rounded-full transition-all duration-200 ${
                  location.pathname.startsWith('/skills-circle')
                    ? 'bg-gray-100'
                    : 'hover:bg-gray-100'
                }`}
                title="Skills Circle"
              >
                <FaUsers className={`w-8 h-8 transition-colors duration-200 ${
                  location.pathname.startsWith('/skills-circle')
                    ? 'text-primary-700'
                    : 'text-gray-600 group-hover:text-primary-700'
                }`} />
              </Link>

              {/* My Skills Communities Icon */}
              <Link
                to="/skills-communities/my-communities"
                className={`relative group p-2 rounded-full transition-all duration-200 ${
                  location.pathname.startsWith('/skills-communities')
                    ? 'bg-gray-100'
                    : 'hover:bg-gray-100'
                }`}
                title="My Skills Communities"
              >
                <span className="relative inline-flex w-8 h-8 items-center justify-center">
                  <FaBook className={`w-7 h-7 transition-colors duration-200 ${
                    location.pathname.startsWith('/skills-communities')
                      ? 'text-primary-700'
                      : 'text-gray-600 group-hover:text-primary-700'
                  }`} />
                  <FaUsers className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white ${
                    location.pathname.startsWith('/skills-communities')
                      ? 'text-primary-700'
                      : 'text-gray-500 group-hover:text-primary-700'
                  }`} />
                </span>
              </Link>

              {/* Notification Bell */}
              <NotificationBell />
            </>
          )}

          {userData ? (
            <div className="relative">
              <button
                type="button"
                className="flex items-center justify-center gap-2 cursor-pointer"
                onClick={handleProfileClick}
              >
                <img
                  src={avatar}
                  alt="profile"
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-gray-300"
                />
                <HiChevronDown className="text-xs text-gray-700 w-4 h-4 flex-shrink-0" />
              </button>
            
            <Transition
              show={showDropdown}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {() => (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 top-full mt-1 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                >
                  <div className="py-1" role="none">
                    <div className="p-4">
                      <div className="flex items-start gap-2">
                        <img
                          src={avatar}
                          alt="profile"
                          className="h-12 w-12 rounded-full object-cover border-2 border-gray-300"
                        />
                        <div className="flex-1">
                          <div className="text-base font-semibold text-gray-800 font-heading">
                            {userData.name}
                          </div>
                          {/* <div className="text-sm text-gray-500">Skill Owner</div> */}
                          <div className="mt-2">
                            <Link 
                              to="/profile"
                              className="border border-primary-700 hover:bg-primary-700 text-primary-600 hover:text-white text-xs font-medium py-1 px-2 rounded transition-colors whitespace-nowrap"
                              onClick={() => setShowDropdown(false)}
                            >
                              View Profile
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr className="my-0.5" />
                    <div className="px-4 py-2">
                      <Link 
                        to="/account-center"
                        className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-action transition-colors"
                        onClick={() => setShowDropdown(false)}
                      >
                        <span>Account Center</span>
                        <IoSettingsOutline className="text-sm" />
                      </Link>
                    </div>
                    <hr className="my-0.5" />
                    <div className="px-4 py-2">
                      <Link 
                        to="#"
                        className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-action transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          logout();
                          setShowDropdown(false);
                        }}
                      >
                        <span>Log Out</span>
                        <IoLogOutOutline className="text-sm" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </Transition>
            </div>
          ) : (
            <Link
              to="/auth"
              className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 text-sm font-medium transition-colors"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default memo(Navbar);
